import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input,
  OnChanges, Output, SimpleChanges,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {A11yModule} from '@angular/cdk/a11y';
import {NgStyle} from '@angular/common';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {finalize, forkJoin, of} from 'rxjs';
import {AdvisorService} from '../../../services/advisor.service';
import {CompanyService} from '../../../services/company.service';
import {AdminService} from '../../../services/admin.service';
import {Advisor} from '../../../shared/model/advisor.model';
import {Company} from '../../../shared/model/company.model';
import {IUser} from '../../../shared/model/user.model';
import {MessageService} from '../../../shared/components/message.service';
import {loadAllPages} from '../../../shared/load-all-pages';

interface Row extends Advisor {
  _tempId: string;
}

/**
 * Eine Firma des ausscheidenden Betreuers samt der Benutzer, die genau diese Firma
 * tragen. `companyId: null` sammelt Benutzer ohne Firma.
 */
interface RemovalGroup {
  companyId: string | null;
  userIds: string[];
  targetAdvisorId: string | null;
}

@Component({
  selector: 'app-advisors-modal',
  templateUrl: './advisors-modal.component.html',
  styleUrl: './advisors-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, A11yModule, NgStyle, MatAutocomplete, MatAutocompleteTrigger, MatOption],
})
export class AdvisorsModalComponent implements OnChanges {
  private readonly advisorService = inject(AdvisorService);
  private readonly companyService = inject(CompanyService);
  private readonly adminService = inject(AdminService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<Advisor[]>();

  rows: Row[] = [];
  companies: Company[] = [];
  usageCounts = new Map<string, number>();
  pendingRemoval: {row: Row; groups: RemovalGroup[]} | null = null;
  isLoading = false;
  isSaving = false;

  private originalSnapshot: Row[] = [];
  private tempCounter = 0;
  private users: IUser[] = [];

  // NP20-736: Autocomplete-Kandidatenliste je Row. Wird pro Row-Aenderung invalidiert
  // (onNameInput, addAdvisor, removeAdvisor, onUserSelected, confirmPendingRemoval,
  // cancel, reload). Ohne Cache liefe filteredUsers() bei OnPush pro Row pro CD-Tick —
  // bei ~200 Betreuern x ~500 Users = 100.000 Iterationen (Reviewer-Hinweis PR #613).
  private filteredUsersCache = new Map<string, IUser[]>();
  private invalidateFilterCache(): void {
    this.filteredUsersCache.clear();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reload();
    }
  }

  private reload(): void {
    this.isLoading = true;
    this.pendingRemoval = null;
    this.stagedReassignments = [];
    this.companyCarriedTo.clear();
    // Alle Seiten: die Listen entscheiden hier, wer beim Loeschen eines Betreuers uebergeben wird.
    forkJoin({
      advisors: loadAllPages<Advisor>((page, size) => this.advisorService.getAdvisors(page, size), 200),
      companies: this.companyService.load(),
      users: loadAllPages<IUser>((page, size) => this.adminService.getUsersPage({}, page, size), 500),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({advisors, companies, users}) => {
          this.companies = [...companies];
          this.users = users;
          this.rows = advisors.map(a => this.toRow(a));
          this.originalSnapshot = this.rows.map(r => this.cloneRow(r));
          this.invalidateFilterCache();
          this.rebuildUsage();
        },
        error: () => this.messageService.error('adminDash.advisors.loadError'),
      });
  }

  private rebuildUsage(): void {
    const map = new Map<string, number>();
    for (const u of this.users) {
      if (!u.superVisorId) continue;
      map.set(u.superVisorId, (map.get(u.superVisorId) ?? 0) + 1);
    }
    this.usageCounts = map;
  }

  private toRow(a: Advisor): Row {
    const linkedUser = a.userId ? this.users.find(u => u.id === a.userId) : undefined;
    return {
      ...a,
      firstName: linkedUser?.firstName ?? a.firstName,
      lastName: linkedUser?.lastName ?? a.lastName,
      email: linkedUser?.email ?? a.email,
      abbreviation: linkedUser?.abbreviation ?? a.abbreviation,
      companyIds: [...(a.companyIds ?? [])],
      _tempId: a.id ?? `new-${++this.tempCounter}`,
    };
  }

  private computeAbbreviation(firstName: string, lastName: string): string {
    const first = (firstName || '').trim().charAt(0);
    const last = (lastName || '').trim().slice(0, 2);
    return (first + last).toUpperCase();
  }

  private cloneRow(r: Row): Row {
    return {...r, companyIds: [...(r.companyIds ?? [])]};
  }

  usageOf(row: Row): number {
    return row.id ? (this.usageCounts.get(row.id) ?? 0) : 0;
  }

  companyById(id: string): Company | undefined {
    return this.companies.find(c => c.id === id);
  }

  toggleFirm(row: Row, companyId: string): void {
    const current = row.companyIds ?? [];
    row.companyIds = current.includes(companyId)
      ? current.filter(id => id !== companyId)
      : [...current, companyId];
  }

  hasFirm(row: Row, companyId: string): boolean {
    return (row.companyIds ?? []).includes(companyId);
  }

  toggleNotification(row: Row): void {
    row.emailNotification = !row.emailNotification;
  }

  toggleStyle(company: Company, active: boolean): {[k: string]: string} {
    if (!active) return {};
    // Bei fehlender/Default-Firmenfarbe (#5F6368) ergibt color-mix(14%, white) einen
    // Ton, der sich vom weissen Chip-Hintergrund praktisch nicht abhebt — die Zuweisung
    // sieht dann inaktiv aus, obwohl sie gesetzt ist. Ohne Inline-Style greift der
    // `[aria-pressed="true"]`-Fallback in der SCSS.
    const color = (company.color || '').trim();
    if (!color || color.toLowerCase() === '#5f6368') return {};
    return {
      background: `color-mix(in srgb, ${color} 14%, white)`,
      color,
      'border-color': `color-mix(in srgb, ${color} 50%, white)`,
    };
  }

  onNameInput(row: Row, raw: string): void {
    const trimmed = (raw ?? '').trim();
    const firstSpace = trimmed.indexOf(' ');
    if (firstSpace < 0) {
      row.firstName = trimmed;
      row.lastName = '';
    } else {
      row.firstName = trimmed.slice(0, firstSpace);
      row.lastName = trimmed.slice(firstSpace + 1).trim();
    }
    row.abbreviation = this.computeAbbreviation(row.firstName, row.lastName);
    this.invalidateFilterCache();
  }

  nameOf(row: Row): string {
    return [row.firstName, row.lastName].filter(Boolean).join(' ');
  }

  filteredUsers(row: Row): IUser[] {
    const cached = this.filteredUsersCache.get(row._tempId);
    if (cached) return cached;
    const term = this.nameOf(row).trim().toLowerCase();
    const takenUserIds = new Set<string>();
    const takenConsultantIds = new Set<string>();
    const takenEmails = new Set<string>();
    for (const r of this.rows) {
      if (r._tempId === row._tempId) continue;
      if (r.userId && r.userId !== '-') takenUserIds.add(r.userId);
      if (r.consultantId && r.consultantId !== '-') takenConsultantIds.add(r.consultantId);
      if (r.email) takenEmails.add(r.email.toLowerCase());
    }
    const pool = this.users.filter(u => {
      if (!u.id) return false;
      if (takenUserIds.has(u.id)) return false;
      if (u.consultantId && takenConsultantIds.has(u.consultantId)) return false;
      if (u.email && takenEmails.has(u.email.toLowerCase())) return false;
      return true;
    });
    const result = !term
      ? pool.slice(0, 20)
      : pool
        .filter(u => {
          const hay = [u.firstName, u.lastName, u.email, u.abbreviation]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(term);
        })
        .slice(0, 20);
    this.filteredUsersCache.set(row._tempId, result);
    return result;
  }

  userLabel(user: IUser): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    const abbr = user.abbreviation ? ` (${user.abbreviation})` : '';
    return `${name}${abbr}`;
  }

  onUserSelected(row: Row, user: IUser): void {
    row.firstName = user.firstName ?? '';
    row.lastName = user.lastName ?? '';
    row.email = user.email ?? '';
    row.abbreviation = user.abbreviation || this.computeAbbreviation(row.firstName, row.lastName);
    row.userId = user.id;
    row.consultantId = user.consultantId;
    this.invalidateFilterCache();
  }

  addAdvisor(): void {
    this.rows = [
      ...this.rows,
      this.toRow({firstName: '', lastName: '', city: ''} as Advisor),
    ];
    this.invalidateFilterCache();
  }

  removeAdvisor(row: Row): void {
    // Ohne den Guard trifft undefined === undefined jeden Benutzer ohne Betreuer.
    const affected = row.id
      ? this.users.filter(u => !!u.id && u.superVisorId === row.id)
      : [];
    if (affected.length > 0) {
      this.pendingRemoval = {row, groups: this.buildRemovalGroups(row, affected)};
      return;
    }
    this.rows = this.rows.filter(r => r._tempId !== row._tempId);
    this.invalidateFilterCache();
  }

  /** Firmen des Betreuers in ihrer Reihenfolge, danach fremde Firmen, Benutzer ohne Firma zuletzt. */
  private buildRemovalGroups(row: Row, affected: IUser[]): RemovalGroup[] {
    const byCompany = new Map<string | null, string[]>();
    for (const u of affected) {
      const key = u.companyId ?? null;
      byCompany.set(key, [...(byCompany.get(key) ?? []), u.id!]);
    }
    const lead = row.companyIds ?? [];
    const ordered: (string | null)[] = [
      ...lead.filter(id => byCompany.has(id)),
      ...[...byCompany.keys()].filter((id): id is string => id !== null && !lead.includes(id)),
      ...(byCompany.has(null) ? [null] : []),
    ];
    return ordered.map(companyId => ({
      companyId,
      userIds: byCompany.get(companyId) ?? [],
      targetAdvisorId: this.defaultTargetFor(row, companyId),
    }));
  }

  get pendingUsageCount(): number {
    return (this.pendingRemoval?.groups ?? []).reduce((sum, g) => sum + g.userIds.length, 0);
  }

  leadsCompany(row: Row, companyId: string | null): boolean {
    return !!companyId && (row.companyIds ?? []).includes(companyId);
  }

  migrationTargetsFor(companyId: string | null): Row[] {
    return this.candidateTargets(this.pendingRemoval?.row._tempId, companyId);
  }

  /** Betreuer der Firma zuerst; die uebrigen bekommen die Firma beim Uebergeben zugewiesen. */
  private candidateTargets(excludeTempId: string | undefined, companyId: string | null): Row[] {
    const rows = this.rows.filter(r => !!r.id && r._tempId !== excludeTempId);
    if (!companyId) return rows;
    return [
      ...rows.filter(r => this.leadsCompany(r, companyId)),
      ...rows.filter(r => !this.leadsCompany(r, companyId)),
    ];
  }

  /** Vorbelegt wird nur ein Betreuer, der genau diese Firma fuehrt; ohne Firma kein Ziel. */
  private defaultTargetFor(row: Row, companyId: string | null): string | null {
    if (!companyId) return null;
    const [first] = this.candidateTargets(row._tempId, companyId);
    if (!first || !this.leadsCompany(first, companyId)) return null;
    return first.id ?? null;
  }

  cancelPendingRemoval(): void {
    this.pendingRemoval = null;
  }

  /**
   * Jede Gruppe braucht ein Ziel: einen Benutzer ohne Betreuer kann das Backend nicht
   * speichern — `superVisorId: null` liest es als "unveraendert" —, und der Betreuer laesst
   * sich anschliessend nicht loeschen, solange noch Benutzer auf ihn zeigen.
   */
  get pendingRemovalResolved(): boolean {
    const groups = this.pendingRemoval?.groups ?? [];
    return groups.length > 0 && groups.every(g => !!g.targetAdvisorId);
  }

  confirmPendingRemoval(): void {
    if (!this.pendingRemoval || !this.pendingRemovalResolved) return;
    const {row, groups} = this.pendingRemoval;
    for (const group of groups) {
      this.stagedReassignments.push({
        fromAdvisorId: row.id!,
        toAdvisorId: group.targetAdvisorId,
        userIds: group.userIds,
      });
      this.assignCompanyToTarget(group);
      this.applyLocalReassignment(group);
    }
    this.rows = this.rows.filter(r => r._tempId !== row._tempId);
    this.pendingRemoval = null;
    this.rebuildUsage();
    this.invalidateFilterCache();
  }

  /**
   * Die Uebergabe steht erst mit dem Speichern beim Server. Lokal mitziehen, damit die
   * Spalte "Zugewiesen" stimmt und eine weitere Loeschung im selben Dialog die
   * uebergebenen Benutzer beim neuen Betreuer findet.
   */
  private applyLocalReassignment(group: RemovalGroup): void {
    const ids = new Set(group.userIds);
    this.users = this.users.map(u =>
      u.id && ids.has(u.id) ? {...u, superVisorId: group.targetAdvisorId ?? undefined} : u,
    );
  }

  /**
   * Fuehrt das Ziel die Firma noch nicht, wandert sie beim Uebergeben mit — sonst
   * haengen die Benutzer an einem Betreuer, der ihre Firma nicht betreut.
   */
  private assignCompanyToTarget(group: RemovalGroup): void {
    if (!group.companyId || !group.targetAdvisorId) return;
    const target = this.rows.find(r => r.id === group.targetAdvisorId);
    if (!target || this.leadsCompany(target, group.companyId)) return;
    target.companyIds = [...(target.companyIds ?? []), group.companyId];
    if (target.id) this.companyCarriedTo.add(target.id);
  }

  private stagedReassignments: {fromAdvisorId: string; toAdvisorId: string | null; userIds: string[]}[] = [];
  private companyCarriedTo = new Set<string>();

  cancel(): void {
    this.rows = this.originalSnapshot.map(r => this.cloneRow(r));
    this.stagedReassignments = [];
    this.companyCarriedTo.clear();
    this.pendingRemoval = null;
    this.invalidateFilterCache();
    this.close();
  }

  save(): void {
    if (this.isSaving) return;
    this.isSaving = true;

    const originalById = new Map(
      this.originalSnapshot.filter(r => r.id).map(r => [r.id!, r]),
    );
    const currentById = new Map(this.rows.filter(r => r.id).map(r => [r.id!, r]));

    const toCreate = this.rows.filter(r => !r.id && this.isRowFilled(r));
    const toDelete = [...originalById.values()].filter(r => !currentById.has(r.id!));
    const toUpdate = [...currentById.values()].filter(r => {
      const orig = originalById.get(r.id!);
      if (!orig || !this.hasChanged(orig, r)) return false;
      // Eine mitgewanderte Firma muss auch an einer Zeile ohne verknuepften Benutzer mitgehen.
      return this.isRowFilled(r) || this.companyCarriedTo.has(r.id!);
    });

    // Ein PATCH je Benutzer, mit dem Ziel aus `users` — dort hat applyLocalReassignment das
    // letzte gewonnene Ziel schon eingetragen. Ueber die staged Liste zu gehen schickte bei zwei
    // Loeschungen hintereinander zwei widerspruechliche PATCHes auf denselben Benutzer.
    const stagedUserIds = new Set(this.stagedReassignments.flatMap(s => s.userIds));
    const stagedUsers = this.users.filter(u => !!u.id && stagedUserIds.has(u.id));
    if (stagedUsers.some(u => !u.superVisorId)) {
      this.isSaving = false;
      this.messageService.error('adminDash.advisors.error.targetRequired');
      return;
    }
    const crudCalls = [
      ...toCreate.map(r => this.advisorService.createAdvisor(this.toPayload(r))),
      ...toUpdate.map(r => this.advisorService.updateAdvisor(this.toPayload(r))),
    ];
    const reassignCalls = stagedUsers
      .map(u => this.adminService.reassignSupervisor(u, u.superVisorId!));
    const deleteCalls = toDelete.map(r => this.advisorService.deleteAdvisor(r.id!));

    const runPhase = (calls: unknown[]) => (calls.length ? forkJoin(calls as any) : of([]));

    // Zuerst die Betreuer-Zeilen, dann die Benutzer, dann die Loeschungen: scheiterte das
    // Uebertragen der mitgewanderten Firma nach dem Umhaengen, haengen die Benutzer an einem
    // Betreuer, der ihre Firma nicht fuehrt. Ziele sind immer schon gespeicherte Zeilen, und
    // der Delete-Guard des Backends braucht allein das Umhaengen vor sich.
    runPhase(crudCalls)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          runPhase(reassignCalls)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                runPhase(deleteCalls)
                  .pipe(
                    finalize(() => {
                      this.isSaving = false;
                      this.cdr.markForCheck();
                    }),
                    takeUntilDestroyed(this.destroyRef),
                  )
                  .subscribe({
                    next: () => {
                      this.stagedReassignments = [];
                      this.companyCarriedTo.clear();
                      this.saved.emit(this.rows.map(r => this.toPayload(r)));
                      this.close();
                    },
                    error: (err: unknown) => this.handleError(err),
                  });
              },
              error: (err: unknown) => {
                this.isSaving = false;
                this.cdr.markForCheck();
                this.handleError(err);
              },
            });
        },
        error: (err: unknown) => {
          this.isSaving = false;
          this.cdr.markForCheck();
          this.handleError(err);
        },
      });
  }

  close(): void {
    this.visible = false;
    this.pendingRemoval = null;
    this.visibleChange.emit(false);
  }

  onOverlayClick(): void {
    this.close();
  }

  private isRowFilled(r: Row): boolean {
    return !!r.userId;
  }

  private hasChanged(a: Row, b: Row): boolean {
    return a.abbreviation !== b.abbreviation
      || a.firstName !== b.firstName
      || a.lastName !== b.lastName
      || a.email !== b.email
      || a.city !== b.city
      || this.companyKey(a) !== this.companyKey(b)
      || !!a.emailNotification !== !!b.emailNotification;
  }

  private companyKey(r: Row): string {
    return [...(r.companyIds ?? [])].sort().join(',');
  }

  private toPayload(r: Row): Advisor {
    const {_tempId: _t, ...rest} = r;
    const first = (r.firstName || '').trim();
    const last = (r.lastName || '').trim();
    // BE-Uebergangs-Workaround: @NotBlank auf city / consultantId / userId / lastName
    // — Modal sammelt diese Felder nicht (per NP20-736-Spec). Bis das BE die
    // Constraints lockert, fuellen wir sie mit '-' bzw. dem Vornamen auf.
    return {
      ...rest,
      firstName: first || '-',
      lastName: last || first || '-',
      city: (r.city || '').trim() || '-',
      consultantId: rest.consultantId || '-',
      userId: rest.userId || '-',
      companyIds: [...(r.companyIds ?? [])],
      emailNotification: !!r.emailNotification,
    };
  }

  private handleError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const key = status === 409 ? 'adminDash.advisors.error.inUse' : 'adminDash.advisors.saveError';
    this.messageService.error(key);
  }
}
